<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: [number, number]
  min?: number
  max?: number
  step?: number
  label?: string
  hideDetails?: boolean
  thumbLabel?: boolean | 'always'
  color?: string
}>(), {
  min: 0,
  max: 100,
  step: 1,
  modelValue: () => [0, 100],
})

const emit = defineEmits<{
  'update:modelValue': [val: [number, number]]
  end: [val: [number, number]]
}>()

const minVal = computed({
  get: () => props.modelValue[0],
  set: (v) => emit('update:modelValue', [Math.min(v, props.modelValue[1]), props.modelValue[1]]),
})

const maxVal = computed({
  get: () => props.modelValue[1],
  set: (v) => emit('update:modelValue', [props.modelValue[0], Math.max(v, props.modelValue[0])]),
})

const minPercent = computed(() => ((minVal.value - props.min) / (props.max - props.min)) * 100)
const maxPercent = computed(() => ((maxVal.value - props.min) / (props.max - props.min)) * 100)

function onEnd() { emit('end', [minVal.value, maxVal.value]) }
</script>

<template>
  <div class="m-range-slider">
    <label v-if="label" class="m-range-slider__label">{{ label }}</label>
    <div class="m-range-slider__track">
      <div class="m-range-slider__fill" :style="{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }" />
      <input type="range" :min="min" :max="max" :step="step" v-model.number="minVal" class="m-range-slider__input" @change="onEnd" />
      <input type="range" :min="min" :max="max" :step="step" v-model.number="maxVal" class="m-range-slider__input" @change="onEnd" />
    </div>
    <div v-if="thumbLabel === 'always'" class="m-range-slider__labels">
      <span>{{ minVal }}</span>
      <span>{{ maxVal }}</span>
    </div>
  </div>
</template>

<style scoped>
.m-range-slider { width: 100%; padding: var(--m-space-2) 0; }

.m-range-slider__label {
  font-size: var(--m-font-size-sm);
  font-weight: 500;
  color: var(--m-on-surface-variant);
  margin-bottom: var(--m-space-2);
  display: block;
}

.m-range-slider__track {
  position: relative;
  height: 4px;
  background: var(--m-border);
  border-radius: 2px;
}

.m-range-slider__fill {
  position: absolute;
  top: 0;
  height: 100%;
  background: var(--m-primary);
  border-radius: 2px;
}

.m-range-slider__input {
  position: absolute;
  top: -6px;
  left: 0;
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  pointer-events: none;
  margin: 0;
}

.m-range-slider__input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--m-primary);
  border: 2px solid #fff;
  box-shadow: var(--m-shadow-sm);
  cursor: pointer;
  pointer-events: auto;
}

.m-range-slider__input::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--m-primary);
  border: 2px solid #fff;
  box-shadow: var(--m-shadow-sm);
  cursor: pointer;
  pointer-events: auto;
}

.m-range-slider__labels {
  display: flex;
  justify-content: space-between;
  font-size: var(--m-font-size-xs);
  color: var(--m-on-surface-variant);
  margin-top: var(--m-space-1);
}
</style>
